<script lang="ts">
	import { Button } from '@infinitro/button';
	import { cfgToThemeCdnPath } from '@infinitro/config';
	import { Verbiage, t } from '@infinitro/content';
	import { parseUtcTimestamp } from '@infinitro/global';
	import { LeftIcon, RightIcon } from '@infinitro/icons';
	import { DotsLoader } from '@infinitro/loading';
	import { afterUpdate, tick } from 'svelte';
	import type { SupportChatMessageRow, SupportChatThreadView } from '../types';
	import {
		isTerminalSupportConversationStatus,
		pendingAgentReplyConversationId
	} from '../support-chat-session';
	import SupportChatBubbleBody from './support-chat-bubble-body.svelte';
	import SupportChatMessageComposer from './support-chat-message-composer.svelte';

	export let brandTitle = '';
	export let hasConversation = false;
	export let lastSubmittedQuestion = '';
	export let question = '';
	export let messageGifUrl = '';
	export let inputElement: HTMLInputElement | null = null;
	export let selectedThreadId: string | null = null;
	/** Server-backed threads; may be empty when the API is unavailable. */
	export let chatThreads: SupportChatThreadView[] = [];
	export let optimisticMessage: SupportChatMessageRow | null = null;
	export let isSendingMessage = false;
	export let isLoadingMore = false;
	export let hasMore = false;
	export let onLoadMoreMessages: (conversationId: string) => Promise<void> = async () => {};
	export let onFocusInput: () => void = () => {};
	export let onInputKeydown: (event: KeyboardEvent) => void = () => {};
	export let onSubmitQuestion: () => void = () => {};
	export let onThreadOpen: (threadId: string) => void = () => {};
	export let onStartNewConversation: () => void = () => {};

	$: activeThread = selectedThreadId
		? (chatThreads.find((t) => t.id === selectedThreadId) ?? {
				id: selectedThreadId,
				title: brandTitle || $t(Verbiage.support),
				preview: '',
				updatedLabel: '',
				messages: []
			})
		: null;

	$: threadStatus = activeThread?.status;
	$: threadIsTerminal = isTerminalSupportConversationStatus(threadStatus);
	/** Typing indicator and AI meta only while the backend is in `ai_handling`. */
	$: threadIsAiHandled =
		threadStatus === 'ai_handling' ||
		(threadStatus === undefined && activeThread?.isAiHandled === true);
	$: threadIsQueuedForHuman = threadStatus === 'queued_for_human';

	$: showAgentTypingBubble =
		threadIsAiHandled &&
		!!selectedThreadId &&
		!!activeThread &&
		!threadIsTerminal &&
		((isSendingMessage && optimisticMessage?.status === 'pending') ||
			($pendingAgentReplyConversationId === selectedThreadId && !isSendingMessage));

	/** True while the UI is waiting on the AI (typing bubble or optimistic send in flight). */
	$: awaitingAgentReplyUi =
		!!selectedThreadId &&
		!!activeThread &&
		(showAgentTypingBubble ||
			(threadIsAiHandled && !!optimisticMessage && optimisticMessage.status === 'pending'));

	let threadEl: HTMLDivElement | null = null;
	/** Bottom sentinel inside `.message-thread` — full scroll once the agent has replied. */
	let threadEndEl: HTMLDivElement | null = null;
	/** While waiting on the AI: keep this row (typing loader or optimistic user) visible at the bottom of the thread viewport. */
	let typingBubbleRowEl: HTMLDivElement | null = null;
	let optimisticBubbleRowEl: HTMLDivElement | null = null;
	let prevThreadId: string | null = null;
	let threadScrollReady = false;
	let prevScrollContentKey = '';
	/** `scrollHeight` after the last `afterUpdate`; used to detect "was pinned to bottom" before new content grows the thread. */
	let prevThreadScrollHeight = 0;
	/** Previous `awaitingAgentReplyUi` — used to detect when an agent reply just landed. */
	let prevAwaitingAgentReplyUi = false;

	function buildScrollContentKey(): string {
		if (!activeThread) return '';
		const tail = activeThread.messages.at(-1)?.body ?? '';
		return [
			activeThread.id,
			String(activeThread.messages.length),
			tail,
			optimisticMessage?.body ?? '',
			String(showAgentTypingBubble),
			String(hasConversation),
			lastSubmittedQuestion
		].join('\u001f');
	}

	/** Scroll so the typing / optimistic row sits at the bottom of `.message-thread` (not the full thread end). */
	function scheduleAwaitingAnchorIntoView(): void {
		void tick().then(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const anchor = showAgentTypingBubble ? typingBubbleRowEl : optimisticBubbleRowEl;
					anchor?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
				});
			});
		});
	}

	afterUpdate(() => {
		// Reset scroll state when the thread is closed so that reopening the same
		// conversation (same id) is treated as a fresh open and scrolls to bottom.
		if (!selectedThreadId) {
			prevThreadId = null;
			threadScrollReady = false;
			prevScrollContentKey = '';
			prevThreadScrollHeight = 0;
			prevAwaitingAgentReplyUi = false;
			return;
		}

		if (!threadEl || !activeThread) return;

		const scrollContentKey = buildScrollContentKey();

		const isNewThread = activeThread.id !== prevThreadId;
		if (isNewThread) {
			prevThreadId = activeThread.id;
			threadScrollReady = false;
			prevScrollContentKey = '';
			prevThreadScrollHeight = 0;
			prevAwaitingAgentReplyUi = false;
		}

		// Wait until there's actual content before doing the initial scroll,
		// since messages load async after the thread opens.
		if (!threadScrollReady) {
			const hasContent =
				activeThread.messages.length > 0 || !!optimisticMessage || showAgentTypingBubble;
			if (hasContent) {
				threadScrollReady = true;
				prevScrollContentKey = scrollContentKey;
				if (awaitingAgentReplyUi) {
					scheduleAwaitingAnchorIntoView();
					void tick().then(() => {
						requestAnimationFrame(() => {
							if (threadEl) prevThreadScrollHeight = threadEl.scrollHeight;
						});
					});
				} else {
					void tick().then(() => {
						requestAnimationFrame(() => {
							if (threadEl) {
								threadEl.scrollTop = threadEl.scrollHeight;
								prevThreadScrollHeight = threadEl.scrollHeight;
							}
						});
					});
				}
			}
			prevAwaitingAgentReplyUi = awaitingAgentReplyUi;
			return;
		}

		if (isLoadingMore) {
			if (threadEl) prevThreadScrollHeight = threadEl.scrollHeight;
			prevAwaitingAgentReplyUi = awaitingAgentReplyUi;
			return;
		}

		const contentChanged = scrollContentKey !== prevScrollContentKey;
		const oldScrollHeight = prevThreadScrollHeight;
		const wasPinnedToBottom =
			oldScrollHeight > 0 && threadEl.scrollTop + threadEl.clientHeight >= oldScrollHeight - 12;

		const agentJustReplied = prevAwaitingAgentReplyUi && !awaitingAgentReplyUi;

		const shouldScrollToEnd =
			threadEndEl &&
			(agentJustReplied || (!awaitingAgentReplyUi && contentChanged && wasPinnedToBottom));

		if (shouldScrollToEnd) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					threadEndEl?.scrollIntoView({ behavior: 'smooth', block: 'end' });
				});
			});
		} else if (awaitingAgentReplyUi && contentChanged) {
			scheduleAwaitingAnchorIntoView();
		}

		prevScrollContentKey = scrollContentKey;
		if (threadEl) prevThreadScrollHeight = threadEl.scrollHeight;
		prevAwaitingAgentReplyUi = awaitingAgentReplyUi;
	});

	async function handleThreadScroll() {
		if (!threadEl || !activeThread || !hasMore || isLoadingMore) return;
		if (threadEl.scrollTop > 80) return;

		const prevScrollHeight = threadEl.scrollHeight;
		await onLoadMoreMessages(activeThread.id);
		await tick();
		if (threadEl) {
			threadEl.scrollTop = threadEl.scrollHeight - prevScrollHeight;
		}
	}

	function openThread(id: string) {
		selectedThreadId = id;
		onThreadOpen(id);
	}

	function closeThread() {
		selectedThreadId = null;
	}

	function startNewFromInbox() {
		onStartNewConversation();
	}

	function formatRelativeElapsed(createdAt?: string): string {
		if (!createdAt) return '';

		try {
			const sentAtMs = parseUtcTimestamp(createdAt);
			if (Number.isNaN(sentAtMs)) return '';
			const diffMs = Math.max(0, Date.now() - sentAtMs);
			const diffMinutes = Math.floor(diffMs / 60_000);

			if (diffMinutes < 60) return `${Math.max(1, diffMinutes)}m`;

			const diffHours = Math.floor(diffMinutes / 60);
			if (diffHours < 24) return `${diffHours}h`;

			const diffDays = Math.floor(diffHours / 24);
			if (diffDays < 30) return `${diffDays}d`;

			const diffMonths = Math.floor(diffDays / 30);
			if (diffMonths < 12) return `${diffMonths}mo`;

			return `${Math.floor(diffDays / 365)}y`;
		} catch {
			return '';
		}
	}

	function isAiAgentMessage(msg: SupportChatMessageRow): boolean {
		const sender = (msg.senderType || '').toLowerCase();
		return sender === 'ai_agent' || sender === 'ai_bot';
	}

	function agentLabelForMessage(msg: SupportChatMessageRow): string {
		if (!isAiAgentMessage(msg)) {
			return (
				activeThread?.agentName?.trim() ||
				activeThread?.title?.trim() ||
				$t(Verbiage.support_chat_human_agent)
			);
		}
		return $t(Verbiage.support_chat_ai_agent);
	}

	function agentMetaLabel(msg: SupportChatMessageRow): string {
		const agentLabel = agentLabelForMessage(msg);
		const elapsed = formatRelativeElapsed(msg.createdAt);

		if (!threadIsAiHandled && !isAiAgentMessage(msg)) {
			if (!elapsed) return agentLabel;
			return $t(Verbiage.support_chat_sender_meta, 'global', {
				sender: agentLabel,
				time: elapsed
			});
		}

		const brandLabel = brandTitle || $t(Verbiage.support);
		if (!elapsed) {
			return $t(Verbiage.support_chat_message_meta_without_time, 'global', {
				brand: brandLabel,
				agent: agentLabel
			});
		}

		return $t(Verbiage.support_chat_message_meta, 'global', {
			brand: brandLabel,
			agent: agentLabel,
			time: elapsed
		});
	}

	function typingMetaLabel(): string {
		const brandLabel = brandTitle || $t(Verbiage.support);
		return $t(Verbiage.support_chat_message_meta_without_time, 'global', {
			brand: brandLabel,
			agent: $t(Verbiage.support_chat_ai_agent)
		});
	}
</script>

<section class="screen-messages">
	{#if !selectedThreadId}
		<div class="inbox-topbar">
			<h2 class="inbox-title">{$t(Verbiage.support_chat_messages)}</h2>
		</div>
		<div class="messages-scroll messages-scroll--inbox">
			<div class="inbox-list">
				{#each chatThreads as thread}
					<button class="inbox-row" type="button" on:click={() => openThread(thread.id)}>
						<div class="inbox-row-copy">
							<p class="inbox-row-title">{thread.title}</p>
							<p class="inbox-row-preview">{thread.preview}</p>
							<p class="inbox-row-meta">{thread.updatedLabel}</p>
						</div>
						<span class="inbox-row-chevron" aria-hidden="true">
							<RightIcon />
						</span>
					</button>
				{/each}
			</div>
			<div class="messages-footer-cta">
				<Button
					text={$t(Verbiage.support_chat_ask_your_question)}
					icon={cfgToThemeCdnPath('/support-chat/send.svg')}
					iconRight={true}
					iconCustomStyles="width: 20px; height: 20px; filter: brightness(0) invert(1);"
					variant="primary"
					fullWidth={true}
					uppercase={false}
					showBackgroundImage={false}
					className="support-cta"
					onClick={startNewFromInbox}
				/>
			</div>
		</div>
	{:else if activeThread}
		<div class="thread-topbar">
			<button
				class="thread-back"
				type="button"
				on:click={closeThread}
				aria-label={$t(Verbiage.back)}
			>
				<span class="thread-back-icon"><LeftIcon /></span>
			</button>
			<h2 class="thread-title">{activeThread.title}</h2>
			<span class="thread-topbar-spacer"></span>
		</div>
		<div class="messages-scroll messages-scroll--thread">
			<div class="message-thread" bind:this={threadEl} on:scroll={handleThreadScroll}>
				{#if isLoadingMore}
					<div class="load-more-spinner" aria-busy="true" aria-label={$t(Verbiage.loading)}>
						<span class="load-more-dot"></span>
						<span class="load-more-dot"></span>
						<span class="load-more-dot"></span>
					</div>
				{/if}

				{#each activeThread.messages as msg}
					<div class="message-row" class:message-row-user={msg.role === 'user'}>
						<div class="message-bubble" class:message-bubble-user={msg.role === 'user'}>
							<SupportChatBubbleBody text={msg.body} />
						</div>
						{#if msg.role === 'agent'}
							<div class="message-meta">{agentMetaLabel(msg)}</div>
						{/if}
					</div>
				{/each}

				{#if optimisticMessage && !activeThread.messages.some((m) => m.role === 'user' && m.body === optimisticMessage?.body)}
					<div class="message-row message-row-user" bind:this={optimisticBubbleRowEl}>
						<div
							class="message-bubble message-bubble-user"
							class:message-bubble-pending={optimisticMessage.status === 'pending'}
							class:message-bubble-failed={optimisticMessage.status === 'failed'}
						>
							<SupportChatBubbleBody text={optimisticMessage.body} />
						</div>
					</div>
				{/if}

				{#if showAgentTypingBubble}
					<div class="message-row message-row-typing" bind:this={typingBubbleRowEl}>
						<div
							class="message-bubble message-bubble-typing"
							aria-busy="true"
							aria-label={$t(Verbiage.loading)}
						>
							<span class="typing-loader-wrapper">
								<DotsLoader color="var(--color-text-500)" />
							</span>
						</div>
						<div class="message-meta">{typingMetaLabel()}</div>
					</div>
				{/if}

				{#if threadIsQueuedForHuman}
					<div class="status-banner status-banner-queue" role="status">
						{$t(Verbiage.support_chat_handoff_notice)}
					</div>
				{/if}

				{#if threadIsTerminal}
					<div class="status-banner status-banner-closed" role="status">
						{threadStatus === 'resolved'
							? $t(Verbiage.support_chat_conversation_resolved)
							: $t(Verbiage.support_chat_conversation_closed)}
					</div>
				{/if}

				{#if hasConversation && !activeThread?.messages?.length && !optimisticMessage}
					<div class="message-row message-row-user">
						<div class="message-bubble message-bubble-user">
							<SupportChatBubbleBody text={lastSubmittedQuestion} />
						</div>
					</div>

					<div class="message-row">
						<div class="message-bubble">
							<SupportChatBubbleBody text={$t(Verbiage.support_chat_agent_reply)} />
						</div>
						<div class="message-meta">{typingMetaLabel()}</div>
					</div>

					<div class="message-row">
						<div class="message-bubble">
							<SupportChatBubbleBody text={$t(Verbiage.support_chat_agent_follow_up)} />
						</div>
						<div class="message-meta">{typingMetaLabel()}</div>
					</div>
				{/if}

				<div class="thread-scroll-anchor" bind:this={threadEndEl} aria-hidden="true"></div>
			</div>

			{#if !threadIsTerminal}
				<SupportChatMessageComposer
					bind:question
					bind:messageGifUrl
					bind:inputElement
					{isSendingMessage}
					{onInputKeydown}
					{onSubmitQuestion}
				/>
			{/if}
		</div>
	{/if}
</section>

<style>
	.screen-messages {
		display: flex;
		height: 100%;
		min-height: 0px;
		flex-direction: column;
		background-color: var(--color-text-100);
}

	.inbox-topbar {
		flex-shrink: 0;
		border-width: 0px;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
}

	.inbox-title {
		text-align: center;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.thread-topbar {
		display: grid;
		flex-shrink: 0;
		grid-template-columns: 32px 1fr 32px;
		align-items: center;
		border-width: 0px;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		border-bottom: 1px solid color-mix(in srgb, var(--color-text-900) 6%, var(--color-text-100));
}

	.thread-back {
		display: flex;
		height: 2rem;
		width: 2rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		color: var(--color-text-900);
		background-color: rgba(var(--color-background-900-rgb), 0.1);
}

	.thread-back:hover {
		background-color: rgba(var(--color-background-900-rgb), 0.16);
	}

	.thread-back-icon {
		display: block;
		height: 1rem;
		width: 1rem;
		color: var(--color-text-900);
		filter: brightness(0.45);
}

	.thread-title {
		min-width: 0px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		text-align: center;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.thread-topbar-spacer {
		display: block;
		height: 2rem;
		width: 2rem;
}

	.messages-scroll {
		display: flex;
		min-height: 0px;
		flex: 1 1 0%;
		flex-direction: column;
		overflow: hidden;
}

	.messages-scroll--inbox {
		display: flex;
		flex: 1 1 0%;
		flex-direction: column;
		overflow-y: auto;
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
}

	.messages-scroll--thread {
		display: flex;
		min-height: 0px;
		flex: 1 1 0%;
		flex-direction: column;
		overflow: hidden;
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
}

	.inbox-list {
		display: flex;
		min-height: 0px;
		flex: 1 1 0%;
		flex-direction: column;
		background-color: var(--color-text-100);
}

	.inbox-row {
		box-sizing: border-box;
		display: flex;
		height: 5.75rem;
		width: 100%;
		flex-shrink: 0;
		align-items: center;
		gap: 0.75rem;
		border-width: 0px;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0px;
		padding-bottom: 0px;
		text-align: left;
		border-bottom: 1px solid color-mix(in srgb, var(--color-text-900) 6%, var(--color-text-100));
		border-radius: 0;
		box-shadow: none;
}

	.inbox-row:hover {
		background-color: color-mix(in srgb, var(--color-secondary-500) 11%, var(--color-text-100));
	}

	.inbox-row:active {
		background-color: color-mix(in srgb, var(--color-secondary-500) 18%, var(--color-text-100));
	}

	.inbox-row-copy {
		display: flex;
		min-height: 0px;
		min-width: 0px;
		flex: 1 1 0%;
		flex-direction: column;
		justify-content: center;
		gap: 0.125rem;
}

	.inbox-row-title {
		margin: 0px;
		flex-shrink: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		line-height: 1.25;
		color: var(--color-text-900);
}

	.inbox-row-preview {
		margin: 0px;
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		font-size: 0.75rem;
		line-height: 1rem;
		line-height: 1.375;
		color: var(--color-text-500);
}

	.inbox-row-meta {
		margin: 0px;
		flex-shrink: 0;
		font-size: 11px;
		font-weight: 500;
		line-height: 1.25;
		color: var(--color-text-500);
}

	.inbox-row-chevron {
		display: block;
		height: 10px;
		width: 10px;
		flex-shrink: 0;
		opacity: 0.5;
		filter: invert(0.35);
}

	.message-thread {
		display: flex;
		flex: 1 1 0%;
		flex-direction: column;
		gap: 0.75rem;
		overflow-y: auto;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 1rem;
		padding-bottom: 1rem;
}

	.messages-footer-cta {
		flex-shrink: 0;
		border-width: 0px;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-bottom: 1rem;
		padding-top: 0.75rem;
		border-top: none;
}

	.message-row {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
}

	.message-row-user {
		align-items: flex-end;
}

	.message-row-typing {
		align-items: flex-start;
	}

	.message-meta {
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 500;
		color: var(--color-text-500);
}

	.status-banner {
		margin-left: auto;
		margin-right: auto;
		max-width: 92%;
		border-radius: 14px;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		text-align: center;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 500;
		line-height: 1.375;
		color: var(--color-text-700);
		background-color: color-mix(in srgb, var(--color-secondary-500) 12%, #ffffff);
}

	.status-banner-closed {
		background-color: color-mix(in srgb, var(--color-text-500) 14%, #ffffff);
	}

	.message-bubble {
		max-width: 88%;
		white-space: pre-wrap;
		overflow-wrap: break-word;
		border-radius: 18px;
		border-bottom-left-radius: 2px;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-900);
		background-color: #ffffff;
		box-shadow: 0 6px 18px rgba(var(--color-background-900-rgb), 0.08);
}

	.message-bubble-user {
		border-radius: 18px;
		border-bottom-right-radius: 2px;
		color: var(--color-text-100);
		background: var(--color-secondary-500);
}

	.message-bubble-pending {
		opacity: 0.6;
	}

	.message-bubble-failed {
		background: var(--color-error-500);
		opacity: 1;
	}

	.message-bubble-typing {
		display: flex;
		min-height: 2.75rem;
		width: -moz-fit-content;
		width: fit-content;
		max-width: 88%;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		align-self: flex-start;
		overflow: visible;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.625rem;
		padding-bottom: 0.625rem;
		line-height: 1;
		min-width: 80px;
}

	/**
	 * DotsLoader is ~4px wide in layout but paints dots via box-shadow to the right, so flex
	 * "centering" misses the visual centroid — nudge left to sit in the bubble middle.
	 */
	.typing-loader-wrapper {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;
		line-height: 0;
}

	.typing-loader-wrapper :global(.loader) {
		position: relative;
		left: -1.5rem;
	}

	.thread-scroll-anchor {
		height: 0px;
		width: 100%;
		flex-shrink: 0;
		overflow: hidden;
}

	.load-more-spinner {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		gap: 0.25rem;
		padding-bottom: 0.5rem;
		padding-top: 0.25rem;
}

	.load-more-dot {
		display: block;
		height: 0.375rem;
		width: 0.375rem;
		border-radius: 9999px;
		background-color: rgba(var(--color-background-900-rgb), 0.28);
		animation: load-more-bounce 1.1s ease-in-out infinite;
}

	.load-more-dot:nth-child(2) {
		animation-delay: 0.18s;
	}

	.load-more-dot:nth-child(3) {
		animation-delay: 0.36s;
	}

	@keyframes load-more-bounce {
		0%,
		80%,
		100% {
			transform: scale(0.75);
			opacity: 0.45;
		}
		40% {
			transform: scale(1);
			opacity: 1;
		}
	}

	:global(.support-cta.infinitro-button) {
		border-radius: 14px;
		border-width: 0px;
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		background-image: var(--gradient-secondary) !important;
}

	.thread-back-icon :global(svg),
	.thread-back-icon :global(img),
	.inbox-row-chevron :global(svg),
	.inbox-row-chevron :global(img) {
		height: 100%;
		width: 100%;
}
</style>
